import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import {
  GET_TEAM_TAGS,
  GET_UNIQUE_COMPANY_NAMES,
  UPDATE_MEMBER_DETAIL,
  UPDATE_TEAM_BOARDING_STATUS,
  UPDATE_TEAM_NOTE,
} from './constants';
import {
  getEventTeamsApi,
  getTeamUniqueCompaniesApi,
  getUsersAssignedToTeamByEventApi,
} from '../../api/event.api';
import {
  createTeamNoteAct,
  getEventTeamListAct,
  getEventTeamListErrorAct,
  getEventTeamListSuccessAct,
  getResponsibleUsersAct,
  getResponsibleUsersErrorAct,
  getResponsibleUsersSuccessAct,
  getTeamTagsErrorAct,
  getTeamTagsSuccessAct,
  getUniqueCompanyNamesErrorAct,
  getUniqueCompanyNamesSuccessAct,
  updateDocumentStatusAct,
  updateMemberDetailErrorAct,
  updateMemberDetailSuccessAct,
  updateTeamBoardingStatusErrorAct,
  updateTeamBoardingStatusSuccessAct,
  updateTeamNoteErrorAct,
  updateTeamNoteSuccessAct,
} from './actions';
import {
  createNoteApi,
  updateBoardingStatusApi,
  updateNoteApi,
} from '../../api/team.api';

import { updateDocumentStatusApi, updateMemberApi } from '../../api/member.api';
import { getErrorMessage } from '../../utils/error.util';
import {
  getEvenGroupTeamUniqueCompaniesApi,
  getEventGroupTeamsApi,
  getUsersAssignedToTeamByEventGroupApi,
} from '../../api/event-group.api';
import { getTeamTagsApi } from '../../api/team-tag.api';
import { addMessage } from '../ToastMessagesManager/actions';
import messages from '../../components/EventTeamDetail/messages';
import teamNoteMessages from '../../components/TeamNotes/messages';

function* fetchTeams({ payload }) {
  try {
    // based on type in payload we need to call different api endpoints
    const { type, id, params, isMerge = false } = payload;
    const api =
      type === 'event-group' ? getEventGroupTeamsApi : getEventTeamsApi;
    const { data } = yield call(api, id, params);
    if (data) {
      yield put(
        getEventTeamListSuccessAct(data, params.page, params.pageSize, isMerge),
      );
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(getEventTeamListErrorAct(errMessage));
  }
}

function* fetchResponsibleUsers({ payload }) {
  try {
    const { type, id } = payload;
    const api =
      type === 'event-group'
        ? getUsersAssignedToTeamByEventGroupApi
        : getUsersAssignedToTeamByEventApi;
    const { data } = yield call(api, id);
    if (data) {
      yield put(getResponsibleUsersSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(getResponsibleUsersErrorAct(errMessage));
  }
}

function* fetchUniqueCompanyNames({ payload }) {
  try {
    const { type, id } = payload;
    const api =
      type === 'event-group'
        ? getEvenGroupTeamUniqueCompaniesApi
        : getTeamUniqueCompaniesApi;
    const { data } = yield call(api, id);
    if (data) {
      yield put(getUniqueCompanyNamesSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(getUniqueCompanyNamesErrorAct(errMessage));
  }
}

function* fetchTags({ payload }) {
  try {
    const { data } = yield call(getTeamTagsApi, payload.params);
    if (data) {
      yield put(getTeamTagsSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(getTeamTagsErrorAct(errMessage));
  }
}

function* updateTeamBoardingStatus({ payload }) {
  try {
    const { data } = yield call(updateBoardingStatusApi, payload);
    if (data) {
      yield put(updateTeamBoardingStatusSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(
      updateTeamBoardingStatusErrorAct({
        id: payload.id,
        boardingStatus: payload.data.boardingStatus,
        error: errMessage,
      }),
    );
  }
}

function* updateMemberDetail({ payload }) {
  try {
    const { data } = yield call(updateMemberApi, payload);
    if (data) {
      yield put(
        updateMemberDetailSuccessAct({
          ...payload,
          data,
        }),
      );
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(
      updateMemberDetailErrorAct({
        ...payload,
        error: errMessage,
      }),
    );
  }
}

function* createTeamNote({ payload }) {
  const { loadingState, teamId, data } = payload;
  if (loadingState === 1) {
    try {
      const { data: createdNote } = yield call(createNoteApi, { teamId, data });
      if (createdNote) {
        yield all([
          put(createTeamNoteAct(2, teamId, createdNote)),
          put(
            addMessage({
              message: teamNoteMessages.noteSuccess,
              variant: 'success',
            }),
          ),
        ]);
      } else {
        throw new Error('Something went wrong');
      }
    } catch (error) {
      const errMessage = getErrorMessage(error);
      yield all([
        put(addMessage({ message: errMessage, variant: 'error' })),
        put(createTeamNoteAct(3, teamId)),
      ]);
    }
  }
}

function* updateTeamNote({ payload }) {
  try {
    const { data } = yield call(updateNoteApi, payload);
    if (data) {
      yield all([
        put(updateTeamNoteSuccessAct(data)),
        put(
          addMessage({
            message: teamNoteMessages.noteUpdate,
            variant: 'success',
          }),
        ),
      ]);
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(
      updateTeamNoteErrorAct({
        ...payload,
        error: errMessage,
      }),
    );
  }
}

function* updateDocumentStatus({ payload }) {
  const { loadingState, memberId, documentId, data } = payload;
  try {
    if (loadingState === 1) {
      const { data: updatedDocument } = yield call(
        updateDocumentStatusApi,
        memberId,
        data,
      );
      if (data) {
        yield all([
          put(
            addMessage({
              message:
                data?.type === 'parental_consent'
                  ? messages.updateParentalConsentSuc
                  : messages.updateMedSuc,
              variant: 'success',
            }),
          ),
          put(
            updateDocumentStatusAct(2, memberId, documentId, updatedDocument),
          ),
        ]);
      } else {
        throw new Error('Something went wrong');
      }
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield all([
      put(addMessage({ message: errMessage, variant: 'error' })),
      put(updateDocumentStatusAct(3, memberId, documentId)),
    ]);
  }
}
// Individual exports for testing
export default function* eventTeamListSaga() {
  //  uncomment this to setp default action
  yield takeLatest(getEventTeamListAct.toString(), fetchTeams);
  yield takeLatest(getResponsibleUsersAct.toString(), fetchResponsibleUsers);
  yield takeLatest(GET_UNIQUE_COMPANY_NAMES, fetchUniqueCompanyNames);
  yield takeLatest(GET_TEAM_TAGS, fetchTags);
  yield takeEvery(UPDATE_TEAM_BOARDING_STATUS, updateTeamBoardingStatus);
  yield takeEvery(UPDATE_MEMBER_DETAIL, updateMemberDetail);
  yield takeEvery(UPDATE_TEAM_NOTE, updateTeamNote);
  yield takeEvery(updateDocumentStatusAct.toString(), updateDocumentStatus);
  yield takeEvery(createTeamNoteAct.toString(), createTeamNote);
}
